import React, { useEffect, useRef } from 'react';
import './SiteCanvasBackground.css';

const IFRAME_SRC = '/blackhole-webgpu.html?v=20260731-smooth1';

/**
 * Intro cinematic: 1 = Shot A (hero), 0 = Shot C (final About framing).
 * Starts during late hero; final pose lands only after About has scrolled well in
 * so the two-shot path has room before settling.
 */
const INTRO_TOP_RANGE_START = 1.65;
const INTRO_TOP_RANGE_END = -0.62;

function computeIntroScrollProgress() {
  const aboutEl = document.getElementById('about');
  // Default to the distant/hero frame so a missing anchor never flashes the
  // full-dolly center pose while the page is still mounting under the loader.
  if (!aboutEl) return 1;
  const top = aboutEl.getBoundingClientRect().top;
  const vh = window.innerHeight;
  const rangeStart = vh * INTRO_TOP_RANGE_START;
  const rangeEnd = vh * INTRO_TOP_RANGE_END;
  const raw = (rangeStart - top) / Math.max(1, rangeStart - rangeEnd);
  const clamped = Math.min(1, Math.max(0, raw));
  return 1 - clamped;
}

/**
 * Single full-viewport WebGPU layer. Stars + nebula + black hole come from the iframe shader.
 * Framing: hero intro dolly + idle/pointer orbit inside the iframe (no About pin path).
 */
const SiteCanvasBackground = () => {
  const iframeRef = useRef(null);
  const rafRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onPointerMove = (event) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      pointerRef.current = {
        x: (event.clientX / w) * 2 - 1,
        y: (event.clientY / h) * 2 - 1
      };
    };
    window.addEventListener('mousemove', onPointerMove, { passive: true });
    return () => window.removeEventListener('mousemove', onPointerMove);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const postFrame = () => {
      const win = iframe.contentWindow;
      if (!win) return;

      try {
        win.postMessage(
          {
            type: 'intro-scroll',
            progress: computeIntroScrollProgress()
          },
          window.location.origin
        );
        win.postMessage(
          {
            type: 'pointer',
            x: pointerRef.current.x,
            y: pointerRef.current.y
          },
          window.location.origin
        );
      } catch {
        // ignore
      }
    };

    const loop = () => {
      postFrame();
      rafRef.current = requestAnimationFrame(loop);
    };

    const onIframeLoad = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(loop);
    };

    iframe.addEventListener('load', onIframeLoad);
    if (iframe.contentWindow?.document?.readyState === 'complete') {
      onIframeLoad();
    }

    return () => {
      iframe.removeEventListener('load', onIframeLoad);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return (
    <div className="siteCanvasBackground" aria-hidden="true">
      <iframe
        ref={iframeRef}
        className="siteCanvasBackground__iframe"
        title="site-background-webgpu"
        src={IFRAME_SRC}
        loading="eager"
      />
    </div>
  );
};

export default SiteCanvasBackground;
